/* Movatic WMS — Frontend API | CesarFilho 932048016
   Arquivo: src/api/workforce.ts
   ----------------------------------------------------------------------------
   Atualizações sem quebrar o que já funciona:

   - Usa o cliente HTTP unificado (Axios) de `src/lib/hooks/http.ts`
     com baseURL/interceptors e fallback de Authorization.
   - Mantém as mesmas funções públicas: listSkills / createSkill /
     updateSkill / deleteSkill / getOperatorSkills / setOperatorSkills.
   - Normaliza respostas paginadas do DRF (se vier {results: [...]})
     para arrays simples.
   - Compatibilidade de token: se existir apenas "token" (legado), copiamos
     para "movatic_token" (esperado pelo interceptor).
   - setOperatorSkills envia { skill_ids: number[] } (compatível com backend).
*/

import {
  apiGet,
  apiPost,
  apiPut,
  apiDelete,
  http, // instancia axios (para getApiBase/diagnóstico)
} from '../lib/hooks/http' // <<< FIX: caminho relativo (sem alias)

/* ============================ Tipagens ============================ */

export type Skill = {
  id: number
  code: string
  name: string
  category?: 'EQUIPMENT' | 'PROCESS'
  is_active: boolean
}

/** Armazém enxuto (dropdown/lista): id/code/name. */
export type WarehouseLite = {
  id: number
  code: string
  name: string
}

/** DTO esperado pela tela de vínculo Operador x Skills (+ Armazéns, ADR-0057). */
export type OperatorSkillsDTO = {
  id: number
  user_name?: string
  /** through: OperatorSkill -> sempre { skill: Skill } */
  skills: { skill: Skill }[]
  /** ADR-0057 F2: company (id) + base (FK id) + autorizados (id/code/name). */
  company?: number | null
  warehouse?: number | null
  warehouses?: WarehouseLite[]
}

/* ======================== Compatibilidade de Token ================= */
;(function ensureTokenCompatibility() {
  const legacy = localStorage.getItem('token')
  if (legacy && !localStorage.getItem('movatic_token')) {
    localStorage.setItem('movatic_token', legacy)
  }
})()

/* ========================== Helpers locais ======================== */

/** Aceita array direto ou envelope do DRF e devolve sempre array. */
function unwrapList<T>(data: any): T[] {
  if (Array.isArray(data)) return data as T[]
  if (data && typeof data === 'object') {
    if (Array.isArray(data.results)) return data.results as T[]
    if (Array.isArray(data.items)) return data.items as T[]
  }
  return data ? [data as T] : []
}

/* ============================= SKILLS ============================= */

export async function listSkills(search = ''): Promise<Skill[]> {
  // page_size=500 (o max_page_size do backend) p/ trazer TODAS (~60). Sem isto vinha só a 1a
  // página (20), escondendo as de PROCESSO (code texto, que ordena DEPOIS das numéricas). O
  // backend só passou a honrar ?page_size quando o SkillViewSet ganhou a paginação (este fix).
  const params: Record<string, unknown> = { page_size: 500 }
  if (search) params.search = search
  const data = await apiGet<any>('/workforce/skills/', params)
  return unwrapList<Skill>(data)
}

export async function createSkill(payload: Partial<Skill>): Promise<Skill> {
  return await apiPost<Skill>('/workforce/skills/', payload)
}

export async function updateSkill(
  id: number,
  payload: Partial<Skill>,
): Promise<Skill> {
  return await apiPut<Skill>(`/workforce/skills/${id}/`, payload)
}

export async function deleteSkill(id: number): Promise<void> {
  await apiDelete<void>(`/workforce/skills/${id}/`)
}

/* ====================== OPERATOR x SKILLS ========================= */

export async function getOperatorSkills(
  operatorId: number,
): Promise<OperatorSkillsDTO> {
  const raw = await apiGet<any>(`/workforce/operators/${operatorId}/skills/`)

  // Normaliza skills: formato oficial ({skill: Skill}[]) OU legado (number[]).
  let skills: { skill: Skill }[] = []
  if (raw && Array.isArray(raw.skills) && raw.skills.length) {
    if (typeof raw.skills[0] === 'object') {
      skills = raw.skills as { skill: Skill }[]
    } else if (typeof raw.skills[0] === 'number') {
      skills = (raw.skills as number[]).map((id: number) => ({
        skill: { id, code: String(id), name: String(id), is_active: true },
      }))
    }
  }

  // Passa SEMPRE company/warehouse/warehouses (ADR-0057), inclusive quando skills=[]
  // (o formato oficial de skills-vazias caía antes no fallback e perdia estes campos).
  return {
    id: Number(raw?.id ?? operatorId),
    user_name: raw?.user_name ?? undefined,
    skills,
    company: raw?.company ?? null,
    warehouse: raw?.warehouse ?? null,
    warehouses: Array.isArray(raw?.warehouses) ? raw.warehouses : [],
  }
}

export async function setOperatorSkills(
  operatorId: number,
  skills: number[],
  extra?: { warehouse_ids?: number[]; base_warehouse_id?: number },
): Promise<OperatorSkillsDTO | void> {
  // Retrocompatível: chamadores antigos passam só (id, skills) -> body = {skill_ids}.
  const body: Record<string, unknown> = { skill_ids: skills }
  if (extra?.warehouse_ids !== undefined)
    body.warehouse_ids = extra.warehouse_ids
  if (extra?.base_warehouse_id !== undefined)
    body.base_warehouse_id = extra.base_warehouse_id
  return await apiPut<OperatorSkillsDTO | void>(
    `/workforce/operators/${operatorId}/skills/`,
    body,
  )
}

/** ADR-0057 F4b: lista os armazéns ATIVOS de uma company (endpoint já existente,
 *  reutilizado — GET /api/core/warehouses/?company_id=&is_active=true&ordering=code). */
export async function listWarehousesByCompany(
  companyId: number,
): Promise<WarehouseLite[]> {
  const data = await apiGet<any>('/core/warehouses/', {
    company_id: companyId,
    is_active: true,
    ordering: 'code',
  })
  return unwrapList<WarehouseLite>(data)
}

/* ====================== Utilidade opcional ======================== */

export function getApiBase() {
  return (http.defaults.baseURL as string) || ''
}

/* ===================== DASHBOARD DE INDICADORES (D16-3a/3b) =================
   Consome GET /api/workforce/dashboard/?days=N. Forma UNICA: {scope, days, empresas[]}.
   empresas tem 1 elem (scope=company) ou N (scope=group/superuser-sem-company).
   ========================================================================== */

export type EmpresaKPIs = {
  company_id: number
  company_name: string
  agora: {
    fila_por_prioridade: { alta: number; media: number; baixa: number }
    fila_total: number
    fila_idade_max_s: number | null
    em_execucao: number
  }
  periodo: {
    aceite_medio_s: number | null
    execucao_media_s: number | null
    execucao_media_por_tipo_s: Record<string, number | null>
    sla_breaches: number
    exec_overruns: number
  }
}

export type DashboardPayload = {
  scope: 'company' | 'group'
  days: number
  empresas: EmpresaKPIs[]
}

export async function getWorkforceDashboard(
  days = 7,
): Promise<DashboardPayload> {
  return await apiGet<DashboardPayload>('/workforce/dashboard/', { days })
}

/* ===================== LOOP DO COLETOR (ADR-0030 F2a) =====================
   Cliente do loop de tarefas do operador. Espelha as actions do TaskViewSet.
   Auth: o Bearer e' injetado pelo interceptor global -> nada a passar aqui.
   INERTE: a F2b liga estas funcoes a' Home; por agora ninguem as chama.
   Paths: claim-next usa hifen; confirm_pick/confirm_putaway usam underscore
   (url_path default do DRF = nome do metodo).
   ========================================================================== */

export type TaskDTO = {
  id: number
  status?: string
  task_type?: unknown
  [k: string]: unknown
}

export type ConfirmPickBody = {
  reservation_id: number
  qty?: number | string
  operator_id?: number
  equipment_id?: number
  /** ADR-0034 F4b-1: codigo lido do produto -> o backend valida contra a linha (opcional). */
  scan?: string
  /** ADR-0033 D6: UUID por gesto de confirmacao, gerado pelo coletor. Fixa-o na
   *  mutation -> um retry (mesma chave) e' idempotente no backend (apply_pick F2 +
   *  confirm_pick F3). Sem chave -> caminho legado por-reserva. */
  idem_key?: string
}

/* ADR-0034 F4b-2 + F4b-6-0: relatorio da sequencia guiada (read-only). current_line traz
 * pick_mode + product_image (so' no passo produto) para a tela de execucao do coletor. */
export type NextStepStep =
  | 'machine'
  | 'support'
  | 'location'
  | 'pick'
  | 'deliver'
  | 'done'
  | 'not_started'
export type NextStepLine = {
  sequence: number
  product_code: string
  qty: string
  location_code: string | null
  reservation_id: number | null
  pick_mode?: 'UNIT' | 'QUANTITY' | null
  product_image?: string | null
}
export type NextStepDTO = {
  task_id: number
  status: string
  current_step: NextStepStep
  step_applicable: boolean
  expected_code: string | null
  current_line: NextStepLine | null
  remaining_lines: number
}

export async function getNextStep(id: number): Promise<NextStepDTO> {
  return await apiGet<NextStepDTO>(`/workforce/tasks/${id}/next-step/`)
}

/** ADR-0029 F3.2d-ii: vincula o suporte (carro) lido a' atribuicao vigente. */
export async function bindSupport(
  id: number,
  handling_unit_code: string,
): Promise<any> {
  return await apiPost<any>(`/workforce/tasks/${id}/bind_support/`, {
    handling_unit_code,
  })
}

/** ADR-0034 F4b-7 D3: entrega o suporte na PICK-CHEIOS (scan da zona) -> fecha a task + convoca.
 *  A HU e' a vinculada na atribuicao (o backend deriva-a; nao vai no body). */
export async function deliverTask(id: number, scan: string): Promise<any> {
  return await apiPost<any>(`/workforce/tasks/${id}/deliver/`, { scan })
}

/** ADR-0038 heartbeat A2: prova-de-vida da sessao (bump last_seen na sessao ativa do operador do
 *  token). O A1 (lazy-close) fecha em 120s quem parar de bater. Sem sessao ativa -> 204 (neutro). */
export async function heartbeatSession(): Promise<void> {
  await apiPost<void>('/workforce/sessions/heartbeat/', {})
}

export async function claimNextTask(
  payload: Record<string, any> = {},
): Promise<TaskDTO> {
  return await apiPost<TaskDTO>('/workforce/tasks/claim-next/', payload)
}

export async function getTaskInbox(
  params?: Record<string, any>,
): Promise<TaskDTO[]> {
  return unwrapList<TaskDTO>(
    await apiGet<any>('/workforce/tasks/inbox/', params),
  )
}

export async function acceptTask(id: number): Promise<TaskDTO> {
  return await apiPost<TaskDTO>(`/workforce/tasks/${id}/accept/`, {})
}

export async function startTask(id: number, scan?: string): Promise<TaskDTO> {
  // F4b-6: o passo endereco passa o code lido -> o backend valida o inicio no endereco certo.
  return await apiPost<TaskDTO>(
    `/workforce/tasks/${id}/start/`,
    scan ? { scan } : {},
  )
}

/* ADR-0034 F4a (=0038 F7): catalogo de motivos de rejeicao + rejeitar a oferta */
export type RejectionReasonDTO = {
  id: number
  code: string
  name: string
  behavior: 'TIMED' | 'MANUAL'
  duration_seconds: number | null
}

export async function getRejectionReasons(): Promise<RejectionReasonDTO[]> {
  return unwrapList<RejectionReasonDTO>(
    await apiGet('/workforce/rejection-reasons/'),
  )
}

export async function rejectTask(
  id: number,
  reasonId: number,
): Promise<TaskDTO> {
  return await apiPost<TaskDTO>(`/workforce/tasks/${id}/reject/`, {
    reason_id: reasonId,
  })
}

export async function confirmPick(
  id: number,
  body: ConfirmPickBody,
): Promise<any> {
  return await apiPost<any>(`/workforce/tasks/${id}/confirm_pick/`, body)
}

export async function confirmPutaway(
  id: number,
  body: { qty?: number | string } = {},
): Promise<any> {
  return await apiPost<any>(`/workforce/tasks/${id}/confirm_putaway/`, body)
}

export async function finishTask(id: number): Promise<TaskDTO> {
  return await apiPost<TaskDTO>(`/workforce/tasks/${id}/finish/`, {})
}

/* ===================== FICHA DE DESEMPENHO (ADR-0037 F1/F3) =================
   Consome GET /api/workforce/operators/{id}/performance/?days=N. Leitura pura.
   Forma espelha o contrato travado no teste backend (test_f1_operator_performance).
   ========================================================================== */
export type OperatorPerformanceDTO = {
  operator: { id: number; user_id: number; nome: string }
  periodo: { days: number; desde: string }
  volume: {
    total: number
    concluidas: number
    em_curso: number
    canceladas: number
  }
  por_status: Record<string, number>
  por_tipo: Record<
    string,
    {
      n: number
      concluidas: number
      tempo_medio_s: number | null
      tempo_mediana_s: number | null
    }
  >
  responsividade_media_s: number | null
  shorts: null // F1.1 (atribuicao via StockMove/apply_pick)
}

export async function getOperatorPerformance(
  operatorId: number,
  days = 30,
): Promise<OperatorPerformanceDTO> {
  return await apiGet<OperatorPerformanceDTO>(
    `/workforce/operators/${operatorId}/performance/`,
    { days },
  )
}
